import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Trophy, TrendingUp, Users, Share2, Heart, MessageSquare, 
  Filter, Award, Zap, ArrowUpRight, BarChart3, TrendingDown,
  Clock, Target, Activity
} from 'lucide-react';
import * as insightsService from '../services/insightsService';
import PageHeader from '../components/layout/PageHeader';

// === CORES & TEMA (Design System ArenaStats) ===
const THEME = {
  primary: '#111111',    // Verde Brand
  gold: '#b48c2c',       // Dourado
  bg: '#f8fafc',         // Fundo slate-50 (Design System)
  white: '#ffffff',
  text: '#1f2937',
  border: '#e5e7eb'
};

// === COMPONENTE 1: CARD DE INSIGHT OFICIAL (Arena Stats) ===
const OfficialInsightCard = ({ insight }) => {
  const getBadgeColor = (type) => {
    const colors = {
      record: 'bg-yellow-500',
      champion: 'bg-purple-500',
      elite_time: 'bg-blue-500',
      trending: 'bg-green-500'
    };
    return colors[insight.category] || 'bg-gray-500';
  };

  const getBadgeIcon = (type) => {
    const icons = {
      record: '🏆',
      champion: '👑',
      elite_time: '⚡',
      trending: '📈'
    };
    return icons[insight.category] || '✨';
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-200 group cursor-pointer hover:shadow-md transition-all h-full min-h-[280px] flex flex-col" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
      {/* Background com Gradiente */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/90 z-10"></div>
      
      {/* Imagem de Fundo */}
      <img 
        src={insight.heroImage || "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=1000&auto=format&fit=crop"} 
        alt={insight.athlete}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />

      {/* Badge de Categoria + Selo Arena Stats */}
      <div className="absolute top-4 left-4 right-4 z-20 flex justify-between items-start">
        <div className={`${getBadgeColor(insight.category)} backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1 shadow-sm`}>
          <span>{getBadgeIcon(insight.category)}</span>
          <span className="capitalize">{insight.category.replace('_', ' ')}</span>
        </div>
        
        {/* Selo Arena Stats Oficial */}
        {insight.author.verified && (
          <div className="bg-white/90 backdrop-blur-md px-2 py-1 rounded-full text-[10px] font-bold flex items-center gap-1 shadow-sm" style={{ color: THEME.primary }}>
            <Award size={12} fill={THEME.gold} stroke={THEME.gold} />
            <span>Arena Stats</span>
          </div>
        )}
      </div>

      {/* Conteúdo (Bottom) */}
      <div className="absolute bottom-0 left-0 w-full p-5 z-20 text-white mt-auto">
        <p className="text-xs font-bold text-gray-300 uppercase tracking-wider mb-1">{insight.title}</p>
        <h3 className="text-2xl font-bold leading-tight mb-2 text-white shadow-black drop-shadow-md">{insight.athlete}</h3>
        
        <div className="flex items-end gap-2 mb-4">
          <span className="text-4xl font-extrabold drop-shadow-md" style={{ color: THEME.gold }}>{insight.statValue}</span>
          <span className="text-sm font-medium text-gray-300 mb-1.5">{insight.statLabel}</span>
        </div>

        {/* Métricas */}
        <div className="flex flex-wrap gap-2 mb-3">
          {insight.metrics?.slice(0, 2).map((metric, idx) => (
            <div key={idx} className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-xs">
              <span className="font-bold">{metric.value}</span> {metric.unit && <span className="opacity-75">{metric.unit}</span>}
            </div>
          ))}
        </div>

        {/* Ações Rápidas */}
        <div className="flex items-center gap-3 pt-3 border-t border-white/20">
          <button className="flex items-center gap-1 text-xs font-semibold hover:opacity-75 transition-opacity">
            <Heart size={16} /> 
            <span>{Math.floor(Math.random() * 1000)}</span>
          </button>
          <button className="flex items-center gap-1 text-xs font-semibold hover:opacity-75 transition-opacity">
            <MessageSquare size={16} /> 
            <span>{Math.floor(Math.random() * 100)}</span>
          </button>
          <button className="ml-auto p-1.5 bg-white/20 rounded-full hover:bg-white/40 transition-colors backdrop-blur-sm">
            <Share2 size={16} />
          </button>
        </div>

        {/* Data Sources Badge */}
        <div className="mt-2 flex items-center gap-1 text-[10px] text-gray-400">
          <Activity size={10} />
          <span>Fontes: {insight.dataSources?.join(', ') || 'Oficial'}</span>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE 2: RANKING PODIUM (Top 3 Social) ===
const SocialPodium = ({ title, data, icon: Icon }) => {
  if (!data || data.length < 3) return null;
  const [first, second, third] = data;

  const PodiumItem = ({ item, rank, height }) => (
    <div className="flex flex-col items-center group cursor-pointer w-full">
      {/* Avatar */}
      <div className="relative mb-[-12px] z-10 transition-transform group-hover:-translate-y-1">
        <div className={`rounded-full border-4 overflow-hidden shadow-lg bg-white ${
          rank === 1 ? 'border-yellow-400 w-20 h-20' : 
          rank === 2 ? 'border-gray-300 w-14 h-14' : 
          'border-orange-400 w-14 h-14'
        }`}>
          <img 
            src={item.photo || `https://ui-avatars.com/api/?name=${item.name}&background=random`} 
            alt={item.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className={`absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-md ${
           rank === 1 ? 'bg-yellow-500' : rank === 2 ? 'bg-gray-400' : 'bg-orange-500'
        }`}>
          {rank}
        </div>
      </div>

      {/* Base do Pódio */}
      <div className={`w-full rounded-t-xl border border-gray-200 flex flex-col items-center justify-end pb-3 pt-6 px-1 text-center transition-all ${
          rank === 1 ? 'bg-gradient-to-b from-yellow-50 to-white' : 'bg-white'
        } ${height}`}>
        <h4 className="font-bold text-gray-900 text-xs md:text-sm leading-tight line-clamp-2">{item.name}</h4>
        <p className="text-[10px] text-gray-500 mb-1 truncate max-w-[80px]">{item.team}</p>
        <p className="font-mono font-bold text-[#111111] text-base md:text-lg">{item.value}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          {Icon && <Icon size={18} className="text-[#111111]" />}
          {title}
        </h3>
        <button className="text-xs font-bold text-[#111111] hover:underline flex items-center gap-1">
          Ver Top 100 <ArrowUpRight size={14} />
        </button>
      </div>

      <div className="flex items-end justify-center gap-2 md:gap-4 h-48">
        <div className="w-1/3 flex justify-center"><PodiumItem item={second} rank={2} height="h-24 md:h-28" /></div>
        <div className="w-1/3 flex justify-center"><PodiumItem item={first} rank={1} height="h-32 md:h-36" /></div>
        <div className="w-1/3 flex justify-center"><PodiumItem item={third} rank={3} height="h-20 md:h-24" /></div>
      </div>
    </div>
  );
};

// === COMPONENTE 3: LISTA DE RECORDS RECENTES (Feed Lateral) ===
const RecordFeedItem = ({ record }) => (
  <div className="flex gap-3 py-3 border-b border-gray-100 last:border-0 hover:bg-gray-50 px-2 rounded-lg transition-colors cursor-pointer">
    <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-[#111111] font-bold text-xs shrink-0 border border-green-100">
      {record.type === 'WR' ? 'WR' : 'CR'}
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-900 leading-snug">
        <span className="font-bold">{record.athlete}</span> quebrou o recorde de {record.event}
      </p>
      <div className="flex items-center gap-2 mt-1">
        <span className="text-xs font-mono font-bold text-white bg-[#111111] px-1.5 py-0.5 rounded">
          {record.time}
        </span>
        <span className="text-xs text-gray-400">• {record.date}</span>
      </div>
    </div>
  </div>
);

// === PÁGINA PRINCIPAL: MOTOR EDITORIAL (INSIGHTS & TRENDS) ===
const StatsPage = () => {
  const [selectedSport, setSelectedSport] = useState('natacao');
  const [viewMode, setViewMode] = useState('for-you'); // for-you | official | community | explore
  const [loading, setLoading] = useState(true);
  
  // Estados de Dados REAIS
  const [officialInsights, setOfficialInsights] = useState([]);
  const [creatorSpotlight, setCreatorSpotlight] = useState([]);
  const [podiums, setPodiums] = useState([]);
  const [recordFeed, setRecordFeed] = useState([]);

  // Carregar dados REAIS ao trocar esporte
  useEffect(() => {
    loadInsights();
  }, [selectedSport, viewMode]);

  const loadInsights = async () => {
    setLoading(true);
    try {
      if (selectedSport === 'natacao') {
        // 1. Official Insights (Destaques da semana)
        const highlightsData = await insightsService.getWeeklyHighlightsCached({ 
          sport: 'natacao', 
          limit: 6 
        });
        setOfficialInsights(highlightsData.data || []);

        // 2. Creator Spotlight (futuro - por enquanto vazio)
        setCreatorSpotlight([]);

        // 3. Podiums (mock por enquanto - pode ser endpoint separado no futuro)
        setPodiums([
          { 
            title: '100m Livre (M)', 
            icon: Zap,
            data: [
              { name: 'Popovici', team: 'Romênia', value: '46.88' },
              { name: 'Chalmers', team: 'Austrália', value: '47.15' },
              { name: 'Pan Zhanle', team: 'China', value: '47.22' }
            ]
          },
          { 
            title: '200m Borboleta (F)', 
            icon: TrendingUp,
            data: [
              { name: 'McIntosh', team: 'Canadá', value: '2:04.06' },
              { name: 'Zhang Yufei', team: 'China', value: '2:05.12' },
              { name: 'Regan Smith', team: 'EUA', value: '2:05.89' }
            ]
          }
        ]);

        // 4. Record Feed (mock - pode vir de vw_insights_record_progression no futuro)
        setRecordFeed([
          { type: 'WR', athlete: 'Sarah Sjöström', event: '50m Livre', time: '23.61', date: 'Ontem' },
          { type: 'CR', athlete: 'Leon Marchand', event: '400m Medley', time: '4:02.50', date: 'Há 2 dias' },
          { type: 'WR', athlete: 'Pan Zhanle', event: '100m Livre', time: '46.80', date: 'Há 3 dias' }
        ]);
      } else {
        // Futebol - manter mock original
        setOfficialInsights([
          {
            id: 1,
            type: 'official',
            author: { id: 'arena_stats', name: 'Arena Stats', type: 'official', verified: true },
            category: 'trending',
            title: 'Artilheiro da Temporada',
            athlete: 'Erling Haaland',
            statValue: '28',
            statLabel: 'Gols',
            heroImage: 'https://i.pinimg.com/736x/8e/5c/24/8e5c249629737130b912239332252277.jpg',
            metrics: [
              { label: 'Clube', value: 'Man City', unit: '' },
              { label: 'Liga', value: 'Premier League', unit: '' }
            ],
            dataSources: ['Opta', 'Premier League'],
            confidence: 1.0
          }
        ]);
        setPodiums([
          { 
            title: 'Gols Marcados', 
            icon: Trophy,
            data: [
              { name: 'Haaland', team: 'Man City', value: 28 },
              { name: 'Harry Kane', team: 'Bayern', value: 24 },
              { name: 'Mbappé', team: 'Real Madrid', value: 21 }
            ]
          }
        ]);
      }
    } catch (error) {
      console.error('[StatsPage] Erro ao carregar insights:', error);
      // Fallback para evitar tela em branco
      setOfficialInsights([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-12 font-sans" style={{ backgroundColor: THEME.bg }}>
      
      {/* 1. HEADER DA PÁGINA */}
      <div className="mb-6">
        <PageHeader
          title="Stats — Arena Stats"
          subtitle="Insights oficiais, rankings e análises (dados do banco)"
          badgeText="STATS"
        />
      </div>

      <div className="bg-white border-b border-gray-200 sticky top-0 z-20" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 py-4">
          
          {/* Seletor de Esporte */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-4">
            <div className="flex bg-gray-100 p-1 rounded-full w-full md:w-auto">
              <button 
                onClick={() => setSelectedSport('futebol')}
                className={`flex-1 md:flex-none px-6 py-1.5 text-xs font-bold rounded-full transition-all ${
                  selectedSport === 'futebol' ? 'bg-white shadow' : 'text-gray-500 hover:text-gray-900'
                }`}
                style={selectedSport === 'futebol' ? { color: THEME.primary } : {}}
              >
                Futebol
              </button>
              <button 
                onClick={() => setSelectedSport('natacao')}
                className={`flex-1 md:flex-none px-6 py-1.5 text-xs font-bold rounded-full transition-all ${
                  selectedSport === 'natacao' ? 'bg-white shadow' : 'text-gray-500 hover:text-gray-900'
                }`}
                style={selectedSport === 'natacao' ? { color: THEME.primary } : {}}
              >
                Natação
              </button>
            </div>
          </div>

          {/* Modo de Visualização (3 Trilhas) */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setViewMode('for-you')}
              className={`px-4 py-2 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                viewMode === 'for-you' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={viewMode === 'for-you' ? { backgroundColor: THEME.primary } : {}}
            >
              <TrendingUp size={14} className="inline mr-1" />
              Para Você
            </button>
            <button
              onClick={() => setViewMode('official')}
              className={`px-4 py-2 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                viewMode === 'official' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={viewMode === 'official' ? { backgroundColor: THEME.primary } : {}}
            >
              <Award size={14} className="inline mr-1" />
              Oficial (Arena Stats)
            </button>
            <button
              onClick={() => setViewMode('community')}
              className={`px-4 py-2 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                viewMode === 'community' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={viewMode === 'community' ? { backgroundColor: THEME.primary } : {}}
            >
              <Users size={14} className="inline mr-1" />
              Comunidade
            </button>
            <button
              onClick={() => setViewMode('explore')}
              className={`px-4 py-2 text-xs font-bold rounded-lg whitespace-nowrap transition-all ${
                viewMode === 'explore' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              style={viewMode === 'explore' ? { backgroundColor: THEME.primary } : {}}
            >
              <BarChart3 size={14} className="inline mr-1" />
              Explorar Dados
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* 2. COLUNA PRINCIPAL (8/12) */}
          <div className="lg:col-span-8 space-y-8">
            
            {/* Trilha 1: OFICIAL (Arena Stats) */}
            {(viewMode === 'for-you' || viewMode === 'official') && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Award className="inline" size={20} style={{ color: THEME.gold }} />
                    Insights Oficiais (Arena Stats)
                  </h2>
                  <button className="text-xs font-bold text-gray-500 hover:underline" style={{ color: THEME.primary }}>
                    Ver histórico
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-auto md:min-h-80">
                  {loading ? (
                    [1,2].map(i => <div key={i} className="bg-white rounded-2xl animate-pulse h-64 md:h-full"></div>)
                  ) : officialInsights.length > 0 ? (
                    officialInsights.slice(0, 4).map(insight => (
                      <OfficialInsightCard key={insight.id} insight={insight} />
                    ))
                  ) : (
                    <div className="col-span-2 bg-white rounded-2xl p-8 text-center border border-gray-200" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                      <Award size={48} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-gray-500">Nenhum insight disponível no momento.</p>
                      <p className="text-sm text-gray-400 mt-1">Novos insights são publicados semanalmente.</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Trilha 2: COMUNIDADE (Creators) */}
            {(viewMode === 'for-you' || viewMode === 'community') && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <Users size={20} style={{ color: THEME.primary }} />
                    Community Spotlight
                  </h2>
                  <button className="text-xs font-bold text-white px-3 py-1.5 rounded-lg shadow-sm transition-all hover:shadow-md" style={{ backgroundColor: THEME.primary }}>
                    Criar Insight
                  </button>
                </div>
                
                <div className="bg-white rounded-xl p-8 text-center border border-gray-200" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                  <Users size={48} className="mx-auto mb-3 text-gray-300" />
                  <p className="text-gray-700 font-medium mb-1">Em breve: Creator Tools</p>
                  <p className="text-sm text-gray-500">
                    Crie suas próprias análises, compare atletas e compartilhe insights com a comunidade.
                  </p>
                  <button className="mt-4 px-4 py-2 text-xs font-bold rounded-lg border-2 transition-all hover:bg-gray-50" style={{ borderColor: THEME.primary, color: THEME.primary }}>
                    Entrar na lista de espera
                  </button>
                </div>
              </section>
            )}

            {/* Trilha 3: EXPLORAR DADOS (Módulos Analíticos) */}
            {viewMode === 'explore' && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 size={20} style={{ color: THEME.primary }} />
                  <h2 className="text-lg font-bold text-gray-800">Módulos Analíticos</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Card: WR/CR Timeline */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.primary}20` }}>
                        <Clock size={24} style={{ color: THEME.primary }} />
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded" style={{ backgroundColor: THEME.gold, color: 'white' }}>WR</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Timeline de Recordes</h3>
                    <p className="text-sm text-gray-600 mb-3">Progressão histórica de WR/CR com eras e contexto (Super Suit, Textile, etc.)</p>
                    <button className="text-xs font-bold flex items-center gap-1" style={{ color: THEME.primary }}>
                      Explorar <ArrowUpRight size={14} />
                    </button>
                  </div>

                  {/* Card: Race Replay */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.primary}20` }}>
                        <Activity size={24} style={{ color: THEME.primary }} />
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-purple-100 text-purple-700">GAPS</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Race Replay (Gaps)</h3>
                    <p className="text-sm text-gray-600 mb-3">Visualização de gaps por split - veja quem liderou em cada volta</p>
                    <button className="text-xs font-bold flex items-center gap-1" style={{ color: THEME.primary }}>
                      Explorar <ArrowUpRight size={14} />
                    </button>
                  </div>

                  {/* Card: Dominance Score */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.primary}20` }}>
                        <Target size={24} style={{ color: THEME.primary }} />
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-blue-100 text-blue-700">Z-SCORE</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Dominance Score</h3>
                    <p className="text-sm text-gray-600 mb-3">Análise de dominância: vencedor vs média do campo (Mean ± SD)</p>
                    <button className="text-xs font-bold flex items-center gap-1" style={{ color: THEME.primary }}>
                      Explorar <ArrowUpRight size={14} />
                    </button>
                  </div>

                  {/* Card: Relay Correction */}
                  <div className="bg-white rounded-xl p-5 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: `${THEME.primary}20` }}>
                        <TrendingDown size={24} style={{ color: THEME.primary }} />
                      </div>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-orange-100 text-orange-700">RELAY</span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">Relay Correction</h3>
                    <p className="text-sm text-gray-600 mb-3">Flying start vs flat start equivalente (~0.65s correction)</p>
                    <button className="text-xs font-bold flex items-center gap-1" style={{ color: THEME.primary }}>
                      Explorar <ArrowUpRight size={14} />
                    </button>
                  </div>
                </div>
              </section>
            )}

            {/* Seção: Pódios (sempre visível exceto no modo Explore) */}
            {viewMode !== 'explore' && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Trophy style={{ color: THEME.gold }} size={20} />
                  <h2 className="text-lg font-bold text-gray-800">Líderes de Estatísticas</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {loading ? (
                    [1,2].map(i => <div key={i} className="bg-white rounded-xl h-64 animate-pulse"></div>)
                  ) : (
                    podiums.map((podium, idx) => (
                      <SocialPodium key={idx} {...podium} />
                    ))
                  )}
                </div>
              </section>
            )}

          </div>

          {/* 3. COLUNA LATERAL (4/12) */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Card: Filtros Rápidos */}
            <div className="bg-white rounded-xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Filter size={16} style={{ color: THEME.primary }} /> Explorar Dados
              </h3>
              <div className="flex flex-wrap gap-2">
                {(selectedSport === 'futebol' 
                  ? ['Gols', 'Assistências', 'Passes', 'Defesa', 'Sub-23'] 
                  : ['Livre', 'Costas', 'Peito', 'Borboleta', 'Medley']
                ).map(tag => (
                  <button 
                    key={tag} 
                    className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium hover:text-white transition-colors"
                    style={{ 
                      '--hover-bg': THEME.primary
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = THEME.primary}
                    onMouseLeave={(e) => e.target.style.backgroundColor = ''}
                    onMouseOver={(e) => e.target.style.color = 'white'}
                    onMouseOut={(e) => e.target.style.color = ''}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            {/* Card: Feed de Records */}
            <div className="bg-white rounded-xl border border-gray-200 p-4" style={{ boxShadow: '0 1px 2px 0 rgba(6, 78, 59, 0.05)' }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-bold text-gray-900">Quebra de Recordes</h3>
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              </div>
              <div className="space-y-1">
                {recordFeed.map((record, idx) => (
                  <RecordFeedItem key={idx} record={record} />
                ))}
              </div>
              <button className="w-full mt-4 py-2 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-lg transition-colors border border-gray-200">
                Ver todos os recordes
              </button>
            </div>

            {/* Card: Convite Premium */}
            <div className="rounded-xl p-5 text-white shadow-lg relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${THEME.primary} 0%, #0b0b0c 100%)` }}>
              <div className="relative z-10">
                <h3 className="font-bold text-lg mb-1 flex items-center gap-2">
                  <Trophy size={20} style={{ color: THEME.gold }} />
                  Arena Pro
                </h3>
                <p className="text-xs text-green-100 mb-4 opacity-90">
                  Acesse análises avançadas, comparativos de atletas e dados de scout profissional.
                </p>
                <button className="w-full py-2 bg-white rounded-lg text-xs font-bold shadow-md hover:bg-gray-100 transition-colors" style={{ color: THEME.primary }}>
                  Desbloquear Estatísticas
                </button>
              </div>
              {/* Elemento decorativo */}
              <div className="absolute -right-4 -bottom-4 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default StatsPage;
